import { useCallback, useEffect, useState } from 'react';
import { Linking, Pressable } from 'react-native';
import Config from '@bonanza-social/toolkit/config';
import { Box, Icon, Text } from '@bonanza-social/moonlight';
import {
  ICON_ROW_H,
  ICON_SIZE,
  LABEL_ROW_H,
  socialMediaData,
  TILE_H,
} from './SocialMediaButtons.constants';
import { logError } from '@bonanza-social/toolkit/logging';

function SocialMediaButtons({ referralCode }: { referralCode?: string }) {
  const [linkSupport, setLinkSupport] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!referralCode) return;

    const link = `${Config.PLATFORM_URL}/join/${referralCode}`;
    (async () => {
      const entries = await Promise.all(
        socialMediaData.map(async (item) => {
          const url = item.getLink(link);
          const canOpen = await Linking.canOpenURL(url).catch(() => false);
          return [item.name, canOpen] as const;
        }),
      );
      setLinkSupport(Object.fromEntries(entries));
    })().catch((err) => logError('Error checking supported links', err));
  }, [referralCode]);

  const openSocialMedia = useCallback(
    async (item: (typeof socialMediaData)[number]) => {
      if (!referralCode) return;
      const link = `${Config.PLATFORM_URL}/join/${referralCode}`;
      const url = item.getLink(link);
      try {
        await Linking.openURL(url);
      } catch (error: unknown) {
        logError('Unable to send social media link', error);
      }
    },
    [referralCode],
  );

  return (
    <Box flexDirection="row" gap="space-200">
      {socialMediaData.map((item) => {
        const isSupported = linkSupport[item.name];
        const disabled = isSupported === false;

        return (
          <Pressable
            key={item.name}
            style={{ flex: 1 }}
            onPress={() => openSocialMedia(item)}
            disabled={disabled}
            hitSlop={8}
          >
            <Box
              backgroundColor="secondaryBackgroundDefault"
              borderRadius="radius-100"
              padding="space-200"
              alignItems="center"
              justifyContent="center"
              height={TILE_H}
            >
              <Box height={ICON_ROW_H} justifyContent="center">
                <Icon
                  name={item.name}
                  height={ICON_SIZE}
                  width={ICON_SIZE}
                  opacity={disabled ? 0.5 : 1}
                />
              </Box>

              <Box
                height={LABEL_ROW_H}
                justifyContent="center"
                marginTop="space-050"
              >
                <Text
                  variant="caption"
                  color={
                    disabled ? 'darkSurfaceDisabled' : 'darkSurfacePrimary'
                  }
                  numberOfLines={1}
                  textAlign="center"
                >
                  {item.text}
                </Text>
              </Box>
            </Box>
          </Pressable>
        );
      })}
    </Box>
  );
}

export default SocialMediaButtons;
